import { useState } from "react";
import { CheckCircle2, Loader2, MessageSquare, Star, X } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../ui/Modal";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerType?: "delayed" | "premium" | "manual";
  title?: string;
  subtitle?: string;
  onSubmitted?: () => void;
}

const RATING_LABELS: Record<number, string> = {
  1: "Needs improvement",
  2: "Fair",
  3: "Good",
  4: "Very good",
  5: "Excellent!",
};

export function FeedbackModal({
  isOpen,
  onClose,
  triggerType = "manual",
  title = "Rate your experience with Soorgaa",
  subtitle = "Your feedback helps us build a better learning platform for Ethiopian university students.",
  onSubmitted,
}: FeedbackModalProps) {
  const { tokens } = useAuth();
  const token = tokens?.accessToken;

  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!isOpen) return null;

  const activeRating = hoverRating || rating;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (rating < 1 || rating > 5) {
      setError("Please select a rating between 1 and 5 stars.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await api.feedback.submit(token, {
        rating,
        comment: comment.trim() || undefined,
        trigger_type: triggerType,
      });
      setSuccess(true);
      if (onSubmitted) onSubmitted();
      setTimeout(() => {
        setSuccess(false);
        setComment("");
        onClose();
      }, 1600);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to submit rating. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDismiss() {
    if (token && triggerType !== "manual") {
      try {
        await api.feedback.dismiss(token);
      } catch {
        // Non-blocking
      }
    }
    onClose();
  }

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      size="sm"
      showCloseButton={false}
      className="p-6 sm:p-7"
    >
      <div className="relative w-full">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleDismiss}
          disabled={submitting}
          className="absolute -top-2 -right-2 p-1.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition cursor-pointer"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {success ? (
          <div className="py-6 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-success/10 text-success flex items-center justify-center mx-auto border border-success/30">
              <CheckCircle2 size={28} />
            </div>
            <h3 className="text-xl font-bold font-display text-foreground">Thank you!</h3>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Your feedback has been recorded and will help us make Soorgaa even better.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="text-center space-y-1.5 pr-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-2 border border-primary/20">
                <MessageSquare size={20} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-display text-foreground">{title}</h3>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>

            {error && (
              <div className="p-2.5 text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-lg text-center">
                {error}
              </div>
            )}

            {/* Star Rating Selector */}
            <div className="flex flex-col items-center gap-1.5 py-1">
              <div className="flex items-center gap-2" onMouseLeave={() => setHoverRating(0)}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    className="p-1 rounded-md transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
                    aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                  >
                    <Star
                      size={32}
                      className={`transition-colors ${
                        star <= activeRating
                          ? "text-warning fill-warning drop-shadow-xs"
                          : "text-muted-foreground/30 hover:text-muted-foreground/60"
                      }`}
                    />
                  </button>
                ))}
              </div>
              <span className="text-xs font-semibold text-primary min-h-[1rem]">
                {RATING_LABELS[activeRating] || ""}
              </span>
            </div>

            {/* Optional Written Comment */}
            <div>
              <label htmlFor="feedback-comment" className="block text-xs font-semibold text-foreground mb-1">
                Tell us more <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <textarea
                id="feedback-comment"
                rows={3}
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="What do you enjoy about the platform, or what would you like us to improve?"
                className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg border border-border bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
              />
              <div className="flex justify-end text-xs text-muted-foreground mt-0.5">
                {comment.length}/500
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-1">
              {triggerType !== "manual" && (
                <button
                  type="button"
                  onClick={handleDismiss}
                  disabled={submitting}
                  className="flex-1 py-2.5 px-3 rounded-md border border-border text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition text-center cursor-pointer"
                >
                  Maybe later
                </button>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-2.5 px-4 rounded-md bg-primary hover:opacity-90 text-on-accent text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-xs disabled:opacity-50 cursor-pointer"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Submitting…
                  </>
                ) : (
                  "Submit rating"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
